import React, { Component } from 'react';
import PageWrap from '../../components/PageWrap/PageWrap';
import {
  Col,
  Row,
  FormControl,
  FormGroup,
  ControlLabel,
  Checkbox,
  Button,
  InputGroup,
  Alert,
  Tab,
  Tabs,
} from 'react-bootstrap';
import {
  inject,
  observer,
} from 'mobx-react';
import RangeSlider from '../../components/Slider/RangeSlider';
import DnDList from '../../components/DnDList/DnDList';
import logger from '../../utils/logger';
import './Discharge.css';
import {
  Form,
  Formik,
  FieldArray,
} from 'formik';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import { ScaleTypes } from '../../stores/discharge-store';
import TruckChargeForm from './TruckChargeForm';
import TruckChargeList from './TruckChargeList';
import MixChargeForm from './MixChargeForm';
import MixChargeList from './MixChargeList';
import { showConfirmModal } from '../../modules/modals/confirmService';
import * as yup from 'yup';
import cx from 'classnames';
import DischargeSequence from './DischargeSequence';
import DischargeScale from './DischargeScale';
import {
  Portlet,
  PortletBody,
  PortletCaption,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import { Dropdown } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { DischargeAmountType } from './DischargeAmountType';
import LoadSizeChargeList from './LoadSizeChargeList';
import LoadSizeChargeForm from './LoadSizeChargeForm';
import PropTypes from 'prop-types';
import InfoTooltip from '../../components/InfoTooltip';
import AuditLog from '../../components/AuditLog/AuditLog';

const AUDIT_LOG_DISCHARGE_MODEL_NAME = 'DischargeSequence';

@inject('dischargeStore', 'mixStore', 'materialStore', 'devicesStore', 'settingsStore', 'notifierStore')
@observer
class Discharge extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mixId: null,
      scaleId: null,
      dischargeSequenceId: null,
      error: '',
      activeTab: 1,
    };
  }

  componentDidMount() {
    this.mounted = true;
    const { mixStore, materialStore, devicesStore, dischargeStore, t } = this.props;
    const promises = [];

    if (!dischargeStore.truckCharge.length) {
      promises.push(dischargeStore.loadCharge());
    }

    if (!dischargeStore.sequences.length) {
      dischargeStore.getSequences();
    }

    if (!materialStore.materials.length) {
      promises.push(materialStore.loadMaterials());
    }
    if (!devicesStore.devices.length) {
      promises.push(devicesStore.loadDevices());
    }
    if (!mixStore.mixDesigns.length) {
      promises.push(mixStore.loadMixDesigns());
    }
    if (!mixStore.allMixes.length) {
      promises.push(mixStore.loadMixes());
    }

    if (promises.length) {
      Promise.all(promises)
        .catch(e => logger.log(e));
    }

    this.schema = yup.object().shape({
      defaultFlowRate: yup.number().min(1).required(t('This field is required!')),
      discharges: yup.array().of(yup.object({
        washStart: yup.number().nullable().min(0).max(100).notRequired(),
        washAmount: yup.number().nullable().min(0).notRequired(),
        start: yup.number().min(0).max(100),
        finish: yup.number().min(0).max(100),
      })),
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  initialData() {
    const { defaultDischargeRate } = this.props.dischargeStore;
    const scales = this.getScales() || [];

    return new DischargeSequence({
      defaultFlowRate: defaultDischargeRate,
      discharges: scales,
    });
  };

  getScales = () => {
    const { mixStore, dischargeStore } = this.props;
    const { mixId } = this.state;

    if (!mixStore.mixDesigns.length || !mixStore.allMixes.length || !mixId) {
      return dischargeStore.getFinalScales() || [];
    }

    return dischargeStore.getFinalScalesForMix(mixId);
  };

  getSequence = () => {
    const { dischargeSequenceId, mixId } = this.state;
    const { dischargeStore } = this.props;
    const sequence = dischargeStore.sequences.slice().find(v => v.id === dischargeSequenceId) || this.initialData();
    let discharges = [];
    let finalScales = [];

    if (mixId) {
      finalScales = dischargeStore.getFinalScalesForMix(mixId) || [];
    }

    sequence?.discharges?.forEach((scale) => {
      const type = dischargeStore.getTypeForScale(scale.scaleId);
      const unit = dischargeStore.getUnitForScale(scale.scaleId);
      let target = 0;

      if (mixId) {
        const index = finalScales.findIndex(v => v.scaleId === scale.scaleId);
        if (index > -1 && scale.scaleId === finalScales[index].scaleId) {
          target = finalScales[index].target;
        }
      }

      discharges.push({
        ...scale,
        type,
        unit,
        target,
      });
    });

    const speeds = dischargeStore.calculateFlowRate(discharges, sequence.defaultFlowRate, sequence.useDiscreteDischarge);

    discharges.forEach((scale, i) => {
      discharges[i].flowRate = speeds[i] ? speeds[i] : 0;
    });

    return new DischargeSequence({
      ...sequence,
      discharges,
    });
  };

  renderSliders = (values, errors, touched, handleChange, setFieldValue, setValues) => {
    const scales = values.discharges.map(v => {
      const s = this.props.devicesStore.getDeviceById(v.scaleId) || {};
      return new DischargeScale({
        ...v,
        scaleName: s.description || '',
      });
    });
    const { dischargeStore, t } = this.props;

    const onDragEnd = (result) => {
      if (!result.destination) {
        return;
      }

      const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
      };

      const s = reorder(
        values.discharges,
        result.source.index,
        result.destination.index
      );

      s.forEach((scale, i) => {
        const isPrimary = !i;

        s[i] = new DischargeScale({
          ...scale,
          isPrimary,
          noWait: isPrimary ? false : !!scale.noWait,
          order: i + 1,
        });

      });

      const speeds = dischargeStore.calculateFlowRate(s, values.defaultFlowRate, values.useDiscreteDischarge);

      s.forEach((scale, i) => {
        s[i].flowRate = speeds[i];
        setFieldValue(`discharges.${i}`, s[i]);
      });
    };

    const onBeforeDragStart = () => {
      this.setState({
        scaleId: null,
      });
    };

    const onSliderChange = (newRange, i) => {
      setFieldValue(`discharges.${i}.start`, newRange[0]);
      setFieldValue(`discharges.${i}.finish`, newRange[1]);

      const scales = values.discharges.slice();
      scales[i].start = newRange[0];
      scales[i].finish = newRange[1];

      const speeds = dischargeStore.calculateFlowRate(scales, values.defaultFlowRate, values.useDiscreteDischarge);

      values.discharges.forEach((scale, i) => {
        setFieldValue(`discharges.${i}.flowRate`, speeds[i]);
      });
    };

    const onTargetChange = (e, i) => {
      const target = Number(e.target.value);
      setFieldValue(e.target.name, target);

      const scales = values.discharges.slice();
      scales[i].target = target;

      const speeds = dischargeStore.calculateFlowRate(scales, values.defaultFlowRate, values.useDiscreteDischarge);

      values.discharges.forEach((scale, i) => {
        setFieldValue(`discharges.${i}.flowRate`, speeds[i]);
      });
    };

    return (
      <DnDList onDragEnd={onDragEnd} onBeforeDragStart={onBeforeDragStart}>
        {scales.map((scale, i) => (
          <Row key={i} id={scale.scaleId}>
            <FieldArray
              name="discharges"
              render={() => (
                <div>
                  <Row>
                    <Col md={12} sm={12}>
                      <h5 className={cx({'text-danger': errors.discharges && errors.discharges[i]})}>
                        <span className={'range-slider-icon fa fa-gears'} onClick={e => {
                          e.stopPropagation();
                          this.setState({
                            scaleId: this.state.scaleId === scale.scaleId ? null : scale.scaleId,
                          });
                        }} />
                        <span>{scale.scaleName ? ` ${scale.scaleName}` : ` Scale ${scale.type} #${scale.scaleId}`}</span>
                      </h5>
                      <RangeSlider
                        id={scale.scaleId}
                        type={scale.type}
                        range={[scale.start || 0, scale.finish || 100]}
                        isPrimary={scale.isPrimary}
                        onChange={(e, newRange) => {
                          e.stopPropagation();
                          onSliderChange(newRange, i);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6} sm={6}>
                      <FormGroup>
                        <ControlLabel>{t('Target')}</ControlLabel>
                        <InputGroup>
                          <FormControl
                            name={'discharges.target'}
                            value={isNaN(values.discharges[i].target) ? '' : values.discharges[i].target}
                            onChange={e => {
                              onTargetChange(e, i);
                            }}
                            type="number"
                            bsSize="sm"
                          />
                          <InputGroup.Addon>{values.discharges[i].unit}</InputGroup.Addon>
                        </InputGroup>
                      </FormGroup>
                    </Col>
                    {scale.type !== ScaleTypes.WATER && (
                      <Col md={6} sm={6}>
                        <FormGroup>
                          <ControlLabel>{t('Flow rate')}</ControlLabel>
                          <InputGroup>
                            <FormControl
                              name={`discharges.${i}.flowRate`}
                              value={values.discharges[i].flowRate || 0}
                              min={0}
                              onChange={handleChange}
                              disabled={!values.useDiscreteDischarge}
                              type="number"
                              bsSize="sm"
                            />
                            <InputGroup.Addon>{values.discharges[i].unit} / {t('sec')}</InputGroup.Addon>
                          </InputGroup>
                        </FormGroup>
                      </Col>
                    )}
                  </Row>
                  {this.state.scaleId === scale.scaleId && this.renderOption(i, values, errors, touched, handleChange, setValues)}
                </div>
              )}
            />
          </Row>
        ))}
      </DnDList>
    );
  };

  renderSequence() {
    const { dischargeStore, settingsStore, t } = this.props;
    const { sequences } = dischargeStore;
    const { configuration } = settingsStore;

    return (
      <div>
        <FormGroup>
          <ControlLabel>{t('Sequence')}</ControlLabel>
          <FormControl
            name="sequence"
            componentClass="select"
            value={this.state.dischargeSequenceId || ''}
            onChange={e => {
              const dischargeSequenceId = e.target.value ? Number(e.target.value) : null;
              this.setState({ dischargeSequenceId, error: '' });
            }}
            bsSize="sm"
          >
            <option value="">{t('New')}</option>
            { sequences.map((option, i) => (
              <option key={i} value={option.id}>
                {option.name ? `${option.name}` : `#${option.id}`} {configuration && option.id === configuration.defaultDischargeSequenceId ? ' - Default' : ''}
              </option>
            ))}
          </FormControl>
        </FormGroup>
      </div>
    );
  };

  renderOption(index, values, errors, touched, handleChange, setValues) {
    const scale = values.discharges[index];
    const { dischargeStore, devicesStore, t } = this.props;
    const { dischargeAmountType } = dischargeStore;
    const scales = devicesStore.scales.filter(v => !v.parentScaleId && !v.decumulative);
    const s = devicesStore.scales.find(v => v.id === scale.scaleId);

    const holdForScaleName = () => {
      const holdForScaleId = Number(values.discharges[index].holdForScaleId);
      if (holdForScaleId) {
        return devicesStore.scales.find(_scale => _scale.id === holdForScaleId).description;
      }
      return '"Wait for scale"';
    };

    const onPrimaryChange = e => {
      const isPrimary = e.target.checked;
      values.discharges[index].isPrimary = isPrimary;

      if (isPrimary) {
        values.discharges[index].start = 0;
        values.discharges[index].finish = 100;
        values.discharges[index].noWait = false;
      }

      values.discharges.sort((x, y) => y.isPrimary - x.isPrimary);
      values.discharges.forEach((v, i) => v.order = i + 1);

      setValues(values);
    };

    return (
      <Row className="discharge-scale-option">
        <span className="discharge-close-option fa fa-close" onClick={() => this.setState({ scaleId: null })} />
        {scale.isPrimary && scale.type !== ScaleTypes.WATER && (
          <Col md={4} sm={4}>
            <FormGroup>
              <ControlLabel>{t('Start at % of head water')}</ControlLabel>
              <InputGroup className={cx({'has-error': errors.discharges && errors.discharges[index] && errors.discharges[index].startAtHeadWater})}>
                <FormControl
                  name={`discharges.${index}.startAtHeadWater`}
                  value={isNaN(values.discharges[index].startAtHeadWater) ? '' : values.discharges[index].startAtHeadWater}
                  onChange={handleChange}
                  type="number"
                  min={0}
                  max={values.discharges[index].finish || 100}
                  bsSize="sm"
                />
                <InputGroup.Addon>%</InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          </Col>
        )}
        <Col md={4} sm={4}>
          <FormGroup>
            <ControlLabel>{t('Start')}</ControlLabel>
            <InputGroup className={cx({'has-error': errors.discharges && errors.discharges[index] && errors.discharges[index].start})}>
              <FormControl
                name={`discharges.${index}.start`}
                value={isNaN(values.discharges[index].start) ? '' : values.discharges[index].start}
                onChange={handleChange}
                disabled={scale.isPrimary}
                type="number"
                min={0}
                max={values.discharges[index].finish || 100}
                bsSize="sm"
              />
              <InputGroup.Addon>%</InputGroup.Addon>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col md={4} sm={4}>
          <FormGroup className={cx({'has-error': errors.discharges && errors.discharges[index] && errors.discharges[index].finish})}>
            <ControlLabel>{t('Finish')}</ControlLabel>
            <InputGroup>
              <FormControl
                name={`discharges.${index}.finish`}
                value={isNaN(values.discharges[index].finish) ? '' : values.discharges[index].finish}
                onChange={handleChange}
                disabled={scale.isPrimary}
                type="number"
                min={values.discharges[index].start || 0}
                max={values.discharges[index].type === ScaleTypes.WATER ? 50 : 100}
                bsSize="sm"
              />
              <InputGroup.Addon>%</InputGroup.Addon>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col md={4} sm={4}>
          <FormGroup>
            <ControlLabel>{t('Delay')}</ControlLabel>
            <InputGroup>
              <FormControl
                name={`discharges.${index}.delay`}
                value={isNaN(values.discharges[index].delay) ? '' : values.discharges[index].delay}
                onChange={handleChange}
                min={0}
                max={50}
                type="number"
                bsSize="sm"
              />
              <InputGroup.Addon>{t('sec')}</InputGroup.Addon>
            </InputGroup>
          </FormGroup>
        </Col>

        {scale.type !== ScaleTypes.WATER && (
          <Col md={4} sm={4}>
            <FormGroup className={cx({'has-error': errors.discharges && errors.discharges[index] && errors.discharges[index].washStart})}>
              <ControlLabel>{t('Wash start')}</ControlLabel>
              <InputGroup>
                <FormControl
                  name={`discharges.${index}.washStart`}
                  value={isNaN(values.discharges[index].washStart) ? '' : values.discharges[index].washStart}
                  onChange={handleChange}
                  min={values.discharges[index].washAmount > 0 ? 75 : 0}
                  max={100}
                  type="number"
                  bsSize="sm"
                />
                <InputGroup.Addon>%</InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          </Col>
        )}

        {scale.isPrimary && !index && (
          <React.Fragment>
            <Col md={4} sm={4}>
              <FormGroup className={cx({'has-error': errors.discharges && errors.discharges[index] && errors.discharges[index].washAmount})}>
                <ControlLabel>{t('Wash Amount')}</ControlLabel>
                <InputGroup>
                  <FormControl
                    name={`discharges.${index}.washAmount`}
                    value={isNaN(values.discharges[index].washAmount) ? '' : values.discharges[index].washAmount}
                    onChange={handleChange}
                    min={0}
                    type="number"
                    bsSize="sm"
                  />
                  <InputGroup.Addon>{values.discharges[index].washAmountType === DischargeAmountType.Unit ? DischargeAmountType.Unit : '%'}</InputGroup.Addon>
                </InputGroup>
              </FormGroup>
            </Col>
            <Col md={4} sm={4}>
              <FormGroup>
                <ControlLabel>{t('Wash Type')}</ControlLabel>
                <FormControl
                  componentClass="select"
                  name={`discharges.${index}.washAmountType`}
                  value={values.discharges[index].washAmountType || ''}
                  onChange={handleChange}
                  bsSize="sm"
                >
                  {dischargeAmountType.map((option, i) => (
                    <option key={i} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
          </React.Fragment>
        )}

        {scale.isPrimary && scale.type === ScaleTypes.AGG && !s?.decumulative && (
          <React.Fragment>
            <Col md={4} sm={4}>
              <FormGroup>
                <ControlLabel>{t('Wait For Scale')}</ControlLabel>
                <FormControl
                  componentClass="select"
                  name={`discharges.${index}.holdForScaleId`}
                  value={values.discharges[index].holdForScaleId || ''}
                  onChange={handleChange}
                  bsSize="sm"
                >
                  <option key={0} value="" />
                  {scales.map((option) => (
                    scale.scaleId !== option.id && (
                      <option key={option.id} value={option.id}>
                        {option.description}
                      </option>
                    )
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
            <Col md={4} sm={4}>
              <FormGroup className={cx({'has-error': errors?.discharges && errors?.discharges[index]?.holdAmount})}>
                <ControlLabel>
                  {t('Hold Amount')}
                  <InfoTooltip text={t(`When ${s.description} passes this threshold, ${s.description} scale will be held until ${holdForScaleName()} finishes OR resume is triggered.`)} />
                </ControlLabel>
                <InputGroup>
                  <FormControl
                    name={`discharges.${index}.holdAmount`}
                    value={isNaN(values.discharges[index].holdAmount) ? '' : values.discharges[index].holdAmount}
                    onChange={handleChange}
                    min={0}
                    type="number"
                    bsSize="sm"
                  />
                  <InputGroup.Addon>{values.discharges[index].holdAmountType === DischargeAmountType.Unit ? DischargeAmountType.Unit : '%'}</InputGroup.Addon>
                </InputGroup>
              </FormGroup>
            </Col>
            <Col md={4} sm={4}>
              <FormGroup>
                <ControlLabel>{t('Hold Type')}</ControlLabel>
                <FormControl
                  componentClass="select"
                  name={`discharges.${index}.holdAmountType`}
                  value={values.discharges[index].holdAmountType || ''}
                  onChange={handleChange}
                  bsSize="sm"
                >
                  {dischargeAmountType.map((option, i) => (
                    <option key={i} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
            <Col md={4} sm={4}>
              <FormGroup className={cx({'has-error': errors?.discharges && errors?.discharges[index]?.resumeAmount})}>
                <ControlLabel>
                  {t('Resume Amount')}
                  <InfoTooltip text={t(`When ${holdForScaleName()} passes this threshold, ${s.description} scale will resume.`)} />
                </ControlLabel>
                <InputGroup>
                  <FormControl
                    name={`discharges.${index}.resumeAmount`}
                    value={isNaN(values.discharges[index].resumeAmount) ? '' : values.discharges[index].resumeAmount}
                    onChange={handleChange}
                    min={0}
                    type="number"
                    bsSize="sm"
                  />
                  <InputGroup.Addon>{values.discharges[index].resumeAmountType === DischargeAmountType.Unit ? DischargeAmountType.Unit : '%'}</InputGroup.Addon>
                </InputGroup>
              </FormGroup>
            </Col>
            <Col md={4} sm={4}>
              <FormGroup>
                <ControlLabel>{t('Resume Type')}</ControlLabel>
                <FormControl
                  componentClass="select"
                  name={`discharges.${index}.resumeAmountType`}
                  value={values.discharges[index].resumeAmountType || ''}
                  onChange={handleChange}
                  bsSize="sm"
                >
                  {dischargeAmountType.map((option, i) => (
                    <option key={i} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
          </React.Fragment>
        )}

        {scale.type === ScaleTypes.WATER && !scale.isPrimary && (
          <Col md={4} sm={4}>
            <FormGroup>
              <Checkbox
                inline
                className="mt-checkbox mt-checkbox-outline"
                name={`discharges.${index}.noWait`}
                value={values.discharges[index].noWait}
                checked={!!values.discharges[index].noWait}
                onChange={handleChange}
              >
                <span/>
              </Checkbox>
              <ControlLabel>{t('No wait')}</ControlLabel>
            </FormGroup>
          </Col>
        )}

        {scale.type !== ScaleTypes.WATER && (
          <>
            <Col md={4} sm={4}>
              <FormGroup>
                <Checkbox
                  inline
                  className="mt-checkbox mt-checkbox-outline"
                  name={`discharges.${index}.withWater`}
                  value={values.discharges[index].withWater}
                  checked={!!values.discharges[index].withWater}
                  onChange={handleChange}
                >
                  <span/>
                </Checkbox>
                <ControlLabel>{t('With Water')}</ControlLabel>
              </FormGroup>
            </Col>
            <Col md={4} sm={4}>
              <FormGroup>
                <Checkbox
                  inline
                  className="mt-checkbox mt-checkbox-outline"
                  name={`discharges.${index}.waitForAdmix`}
                  value={values.discharges[index].waitForAdmix}
                  checked={!!values.discharges[index].waitForAdmix}
                  onChange={handleChange}
                >
                  <span/>
                </Checkbox>
                <ControlLabel>{t('Wait For Admix')}</ControlLabel>
              </FormGroup>
            </Col>
          </>
        )}

        {dischargeStore.isOneLevelScale(scale.scaleId) && (
          <Col md={4} sm={4}>
            <FormGroup>
              <Checkbox
                inline
                className="mt-checkbox mt-checkbox-outline"
                name={`discharges.${index}.isPrimary`}
                value={values.discharges[index].isPrimary}
                checked={!!values.discharges[index].isPrimary}
                onChange={onPrimaryChange}
              >
                <span/>
              </Checkbox>
              <ControlLabel>{t('As primary')}</ControlLabel>
            </FormGroup>
          </Col>
        )}

      </Row>
    );
  }

  renderConfig(values, errors, touched, handleChange, setFieldValue) {
    const { dischargeStore, t } = this.props;

    const onDefaultFlowRateChange = (e) => {
      const target = Number(e.target.value);
      setFieldValue(e.target.name, target);

      const scales = values.discharges.slice();
      const speeds = dischargeStore.calculateFlowRate(scales, target, values.useDiscreteDischarge);

      values.discharges.forEach((scale, i) => {
        setFieldValue(`discharges.${i}.flowRate`, speeds[i]);
      });
    };

    return (
      <div>
        <FormGroup className={cx({'has-error': errors.defaultFlowRate && touched.defaultFlowRate})}>
          <ControlLabel>{t('Default auto discharge rate')}</ControlLabel>
          <InputGroup>
            <FormControl
              name="defaultFlowRate"
              type="number"
              value={values.defaultFlowRate || ''}
              onChange={onDefaultFlowRateChange}
              min={1}
              bsSize="sm"
            />
            <InputGroup.Addon>{t('unit')} / {t('sec')}</InputGroup.Addon>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name="allWaterOnFirstDrop"
            value={values.allWaterOnFirstDrop}
            checked={!!values.allWaterOnFirstDrop}
            onChange={handleChange}
          >
            <span />
          </Checkbox>
          <ControlLabel>{t('Batch all head water on first drop')}</ControlLabel>
        </FormGroup>
        <FormGroup>
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name="allWashWaterOnLastDrop"
            value={values.allWashWaterOnLastDrop}
            checked={!!values.allWashWaterOnLastDrop}
            onChange={handleChange}
          >
            <span />
          </Checkbox>
          <ControlLabel>{t('Batch all wash water on last drop')}</ControlLabel>
        </FormGroup>
        <FormGroup>
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name="holdHeadWaterUntilAdmixFinish"
            value={values.holdHeadWaterUntilAdmixFinish}
            checked={!!values.holdHeadWaterUntilAdmixFinish}
            onChange={handleChange}
          >
            <span />
          </Checkbox>
          <ControlLabel>{t('Hold head water until admix finish')}</ControlLabel>
        </FormGroup>
        <FormGroup>
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name="holdWashWaterUntilAdmixFinish"
            value={values.holdWashWaterUntilAdmixFinish}
            checked={!!values.holdWashWaterUntilAdmixFinish}
            onChange={handleChange}
          >
            <span />
          </Checkbox>
          <ControlLabel>{t('Hold wash water until admix finish')}</ControlLabel>
        </FormGroup>
        <FormGroup>
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name="useDiscreteDischarge"
            value={values.useDiscreteDischarge}
            checked={!!values.useDiscreteDischarge}
            onChange={handleChange}
          >
            <span />
          </Checkbox>
          <ControlLabel>{t('Use Discrete Discharge')}</ControlLabel>
        </FormGroup>
        <FormGroup>
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name="noFlowControl"
            value={values.noFlowControl}
            checked={!!values.noFlowControl}
            onChange={handleChange}
          >
            <span />
          </Checkbox>
          <ControlLabel>{t('No Flow Control')}</ControlLabel>
        </FormGroup>
      </div>
    );
  }

  renderMixes() {
    const { mixStore, t } = this.props;
    const { mixOptions } = mixStore;

    return (
      <FormGroup>
        <ControlLabel>{t('Load target from Mix')}</ControlLabel>
        <Dropdown onChange={(e, data) => this.setState({ mixId: data.value })}
          value={this.state.mixId || ''}
          fluid search selection options={mixOptions} />
      </FormGroup>
    );
  };

  renderDischarge() {
    const { t } = this.props;
    const { dischargeSequenceId, mixId } = this.state;

    return (
      <Formik
        enableReinitialize
        initialValues={
          dischargeSequenceId || mixId ? this.getSequence() : this.initialData()
        }
        validationSchema={this.schema}
        validateOnChange={true}
        onSubmit={this.onSubmit}
      >
        {props => {
          const {
            handleSubmit,
            handleChange,
            setFieldValue,
            setValues,
            values,
            errors,
            touched,
            isSubmitting,
            resetForm,
            submitForm,
          } = props;

          return (
            <Form className={'batch-material-form'}>
              <Row>
                <Col lg={4} md={4} sm={12}>
                  {this.renderSequence(dischargeSequenceId)}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  <FormGroup className={cx({'has-error': !!this.state.error} )}>
                    <ControlLabel>{t('Name')}</ControlLabel>
                    <FormControl
                      name="name"
                      type="text"
                      value={values.name || ''}
                      onChange={e => {
                        handleChange(e);
                        if (this.state.error) {
                          this.setState({ error: '' });
                        }
                      }}
                      bsSize="sm"
                    />
                    {!!this.state.error && <Alert bsStyle="danger">{this.state.error}</Alert>}
                  </FormGroup>
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {this.renderMixes()}
                </Col>
              </Row>
              <Row>
                <Col lg={8} md={8} sm={12}>
                  {this.renderSliders(values, errors, touched, handleChange, setFieldValue, setValues)}
                </Col>
                <Col lg={4} md={4} sm={12}>
                  {this.renderConfig(values, errors, touched, handleChange, setFieldValue)}
                </Col>
              </Row>
              <Row>
                <Col md={12} sm={12}>
                  {this.renderSubmit(handleSubmit, submitForm, resetForm, isSubmitting)}
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    );
  };

  onRemoveSequence = resetForm => {
    const { notifierStore, t } = this.props;
    const id = this.state.dischargeSequenceId;

    showConfirmModal({
      title: t('Do you want to remove sequence?', { id }),
      okLabel: t('Yes'),
      cancelLabel: t('No'),
      enableEscape: false,
    }).then(() => {

      this.props.dischargeStore.removeSequence(id).then(() => {

        this.setState({
          message: t('Sequence successfully removed!', { id }),
          mixId: null,
          dischargeSequenceId: null,
          scaleId: null,
          error: '',
        });

        resetForm({
          values: this.initialData(),
        });

      }).catch(e => {
        notifierStore.showError({ key: 'Can\'t remove sequence:', props: { error: e.message }});
      });

    });

  };

  onResetForm = resetForm => {
    resetForm({
      values: this.initialData(),
    });

    this.setState({
      mixId: null,
      dischargeSequenceId: null,
      scaleId: null,
      error: '',
    });
  };

  onSubmit = (values, actions) => {
    const { dischargeStore, notifierStore } = this.props;
    let promise;
    values.discharges.forEach((v, i) => v.order = i + 1);
    this.setState({
      error: '',
    });

    if (!values.name || !values.name.trim()) {
      const error = this.props.t('This field is required!');
      this.setState({ error });
      actions.setSubmitting(false);
      return;
    }

    if (values.id) {
      promise = dischargeStore.updateSequence(values);
    } else {
      promise = dischargeStore.addSequence(values);
    }

    promise.then(data => {
      if (!this.mounted) {
        return;
      }

      this.setState({
        mixId: null,
        dischargeSequenceId: data.id,
      });

      actions.setSubmitting(false);
    }).catch(e => {
      if (!this.mounted) {
        return;
      }

      actions.setSubmitting(false);
      notifierStore.showError({ key: 'Error', props: { error: e.message }});

    });

  };

  renderSubmit(handleSubmit, submitForm, resetForm, isSubmitting) {
    const { t } = this.props;
    const { dischargeSequenceId } = this.state;

    return (
      <Row>
        <Col sm={6}>
          {!!dischargeSequenceId &&
          <Button onClick={() => this.onRemoveSequence(resetForm)}>{t('Remove')}</Button>
          }
        </Col>
        <Col sm={6} className="text-right">
          <Button onClick={() => this.onResetForm(resetForm)}>{t('Reset')}</Button>
          {' '}
          <LoadingButton
            bsStyle="primary"
            label={t('Save')}
            type="submit"
            loadingLabel={t('Saving')}
            isLoading={isSubmitting}
          />
        </Col>
      </Row>
    );
  };

  render() {
    const { dischargeStore, t } = this.props;
    const { truckChargeId, mixChargeId, loadSizeChargeId } = dischargeStore;
    const { activeTab, dischargeSequenceId } = this.state;

    const onTabSelect = (tab) => {
      this.setState({ activeTab: tab });
    };

    return (
      <PageWrap page={t('nav.Discharge')}>
        <Row>
          <Col lg={7} md={12} sm={12}>
            <Portlet className="discharge-wrap">
              <PortletTitle>
                <PortletCaption subject={t('nav.Discharge')}/>
              </PortletTitle>
              <PortletBody>
                <Tabs defaultActiveKey={activeTab} id="mix" onSelect={onTabSelect}>
                  <Tab eventKey={1} title={t('Sequence')}>
                    {this.renderDischarge()}
                  </Tab>
                  <Tab eventKey={2} title={t('Audit Log')}>
                    <Row>
                      <Col lg={4} md={12} sm={12}>
                        {this.renderSequence(dischargeSequenceId)}

                        {!dischargeSequenceId && (
                          <p>{t('Please select the sequence')}</p>
                        )}
                      </Col>
                    </Row>
                    {activeTab === 2 && dischargeSequenceId && (
                      <AuditLog
                        modelId={dischargeSequenceId}
                        modelName={AUDIT_LOG_DISCHARGE_MODEL_NAME}
                      />
                    )}
                  </Tab>
                </Tabs>
              </PortletBody>
            </Portlet>
          </Col>
          <Col lg={5} md={12} sm={12}>
            {truckChargeId &&
            <TruckChargeForm />
            }
            <TruckChargeList />
            {mixChargeId &&
            <MixChargeForm />
            }
            <MixChargeList />
            {loadSizeChargeId &&
            <LoadSizeChargeForm />
            }
            <LoadSizeChargeList />
          </Col>
        </Row>
      </PageWrap>
    );
  }
}

Discharge.propTypes = {
  devicesStore: PropTypes.object,
  dischargeStore: PropTypes.object,
  mixStore: PropTypes.object,
  materialStore: PropTypes.object,
  notifierStore: PropTypes.object,
  settingsStore: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(Discharge);
